"use client";

import { useEffect, useRef, type DependencyList } from "react";

/**
 * A WebMCP tool, matching the browser `document.modelContext.registerTool` shape.
 * `inputSchema` is a JSON Schema object. `execute` receives the parsed args and
 * returns either an MCP result or a plain string (normalized to a result below).
 */
export interface McpToolConfig {
  name: string;
  description: string;
  inputSchema?: Record<string, unknown>;
  execute: (
    args: Record<string, unknown>
  ) => Promise<McpToolResult | string> | McpToolResult | string;
}

export interface McpToolResult {
  content: Array<{ type: "text"; text: string }>;
  isError?: boolean;
}

/** Wrap a plain string in the MCP text-content result shape. */
export const text = (s: string, isError = false): McpToolResult => ({
  content: [{ type: "text", text: s }],
  isError,
});

// The @mcp-b/global runtime touches navigator/document and auto-initializes on
// import, so it must load in the browser only. Loaded once, lazily, on first use.
let runtime: Promise<unknown> | null = null;
const ensureRuntime = () => (runtime ??= import("@mcp-b/global"));

type ModelContext = {
  registerTool: (
    tool: McpToolConfig,
    opts?: { signal?: AbortSignal }
  ) => unknown;
};
// The May 27, 2026 WebMCP draft moved modelContext from Navigator to Document.
// Prefer document; fall back to navigator for older @mcp-b/global versions.
// `??` short-circuits so navigator's deprecated getter is only touched when
// document has no modelContext (avoids the deprecation warning on current runtimes).
const getModelContext = (): ModelContext | undefined =>
  (globalThis as { document?: { modelContext?: ModelContext } }).document
    ?.modelContext ??
  (globalThis as { navigator?: { modelContext?: ModelContext } }).navigator
    ?.modelContext;

/**
 * Registers a WebMCP tool for the lifetime of the calling component. Loads the
 * browser MCP runtime on first use, and unregisters on unmount / dep change via
 * an AbortController (also covers React StrictMode's mount/unmount/remount).
 *
 * `deps` behaves like useEffect's dependency array: change it to re-register
 * (e.g. when the tool's identity or captured data changes).
 *
 * `enabled` (default true) gates registration — pass false to keep the hook call
 * unconditional (React rules) while not exposing the tool, e.g. for tools that
 * only apply in some contexts.
 */
export function useMcpTool(
  config: McpToolConfig,
  deps: DependencyList = [],
  enabled = true
) {
  // Keep the latest config in a ref so the registered tool always runs the
  // current handler. This decouples (re)registration from handler identity —
  // callers pass inline closures without memoizing. `deps` should reflect only
  // what changes the tool's identity/schema (e.g. its name).
  const configRef = useRef(config);
  useEffect(() => {
    configRef.current = config;
  });

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    ensureRuntime().then(() => {
      if (controller.signal.aborted) return;
      const mc = getModelContext();
      if (!mc) return;
      const { name, description, inputSchema } = configRef.current;
      mc.registerTool(
        {
          name,
          description,
          inputSchema,
          execute: async (args) => {
            try {
              const result = await configRef.current.execute(args ?? {});
              return typeof result === "string" ? text(result) : result;
            } catch (err) {
              return text(
                err instanceof Error ? err.message : "Tool execution failed",
                true
              );
            }
          },
        },
        { signal: controller.signal }
      );
    });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);
}
